// src/security/axiosAuth.ts
import axios, { AxiosInstance, InternalAxiosRequestConfig, AxiosHeaders } from "axios";
import { getToken, getLegacyTsToken } from "./tokenStorage";
import { forceLogout } from "./logout";

function pad(n: number, len = 2) { return String(n).padStart(len, "0"); }
function tsUTCString(ms: number): string {
    const d = new Date(ms);
    return (
        d.getUTCFullYear().toString() +
        pad(d.getUTCMonth() + 1) +
        pad(d.getUTCDate()) +
        pad(d.getUTCHours()) +
        pad(d.getUTCMinutes()) +
        pad(d.getUTCSeconds()) +
        pad(d.getUTCMilliseconds(), 3)
    );
}

const REQ_INSTALLED = Symbol.for("auth-ts-interceptor-installed");
const RESP_INSTALLED = Symbol.for("auth-ts-response-interceptor-installed");

export function installAxiosAuth(options?: { instance?: AxiosInstance; apiKey?: string }) {
    const target = options?.instance ?? axios;

    // ---------- Request Interceptor ----------
    if (!(target as any)[REQ_INSTALLED]) {
        (target as any)[REQ_INSTALLED] = true;
        const apiKey = options?.apiKey;

        target.interceptors.request.use((config: InternalAxiosRequestConfig) => {
            // 1) Headers (dein Teil)
            const headers = AxiosHeaders.from(config.headers);
            const token = getToken();
            if (token) headers.set("Authorization", `Bearer ${token}`);
            headers.set("x-ts", tsUTCString(Date.now()));
            // Header behalten ist okay, aber Backend erwartet zusätzlich ?auth=
            if (apiKey) headers.set("x-apikey", apiKey);
            config.headers = headers;

            // 2) Query-Parameter mergen: ?auth=... & ggf. &tstoken=...
            const merged = new URLSearchParams();

            // bestehende params aus Axios übernehmen (egal ob Objekt oder URLSearchParams)
            if (config.params instanceof URLSearchParams) {
                config.params.forEach((v, k) => merged.set(k, v));
            } else if (config.params && typeof config.params === "object") {
                Object.entries(config.params).forEach(([k, v]) => {
                    if (v != null) merged.set(k, String(v));
                });
            }

            if (apiKey) merged.set("auth", apiKey); // <— WICHTIG: Backend-Expect
            const ts = getLegacyTsToken();
            if (ts) merged.set("tstoken", ts);

            // zurück an Axios (als plain object, damit Axios später nicht doppelt encodiert)
            config.params = Object.fromEntries(merged.entries());

            return config;
        });
    }

    // ---------- Response Interceptor ----------
    if (!(target as any)[RESP_INSTALLED]) {
        (target as any)[RESP_INSTALLED] = true;

        target.interceptors.response.use(
            (resp) => resp,
            (error) => {
                const status = error?.response?.status;
                const msg: string =
                    error?.response?.data?.message ||
                    error?.response?.data?.error ||
                    "";

                const isWrongCreds =
                    typeof msg === "string" && msg.toLowerCase().includes("wrong credentials");

                if (status === 401 || status === 403 || isWrongCreds) {
                    forceLogout("Login abgelaufen oder ungültig.");
                }
                return Promise.reject(error);
            }
        );
    }
}
